import { Controller } from "@hotwired/stimulus"

// CSP-safe replacements for the Meetings module's legacy inline onclick
// handlers. The production Content-Security-Policy ships without
// 'unsafe-inline', so inline onclick="" attributes are blocked (they can't
// carry a nonce) — every meeting button that used one silently did nothing on
// staging/prod. Mounted once on the meetings list table and once on the
// meeting show page; each old handler becomes a declarative data-action.
export default class extends Controller {
  static targets = [
    "deleteModal", "deleteModalName", "deleteModalSeriesHint", "deleteModalSoloHint",
    "deleteForm", "deleteScope", "deleteSeriesButton"
  ]

  // ── Delete scope modal (Larry 2026-08-06) ──────────────────────────
  // Kebab Delete opens the modal; series meetings offer "just this one"
  // vs "this + all upcoming", solo meetings read as a plain confirm.
  confirmDelete(event) {
    this._closeDropdowns()
    const isSeries = String(event.params.series) === "true"
    this.deleteFormTarget.action = event.params.url
    this.deleteModalNameTarget.textContent = event.params.name || ""
    this.deleteModalSeriesHintTarget.classList.toggle("hidden", !isSeries)
    this.deleteModalSoloHintTarget.classList.toggle("hidden", isSeries)
    this.deleteSeriesButtonTarget.classList.toggle("hidden", !isSeries)
    this.deleteModalTarget.classList.remove("hidden")
  }

  closeDeleteModal() {
    this.deleteModalTarget.classList.add("hidden")
  }

  deleteJustThis() {
    this.deleteScopeTarget.value = ""
  }

  deleteWholeSeries() {
    this.deleteScopeTarget.value = "series"
  }

  // Row navigation on the meetings list. Skips when the user is selecting text
  // so copying a cell doesn't trigger a page change.
  // data-meeting-ui-href-param="/projects/…/meetings/…"
  visit(event) {
    if (window.getSelection().toString()) return
    const href = event.params.href
    if (!href) return
    // A Turbo visit, not a full page load: a full load ended a feedback
    // screen recording that was going (Kloepfer K3-34).
    if (window.Turbo?.visit) window.Turbo.visit(href)
    else window.location = href
  }

  // Reveal a form/panel by id, then optionally scroll it into view and focus
  // its first field. Also closes any open dropdown menu (these actions are
  // usually fired from inside one). params: id, focus(bool), scroll(bool)
  reveal(event) {
    this._closeDropdowns()
    const el = event.params.id && document.getElementById(event.params.id)
    if (!el) return
    el.classList.remove("hidden")
    if (event.params.scroll) el.scrollIntoView({ block: "center" })
    if (event.params.focus) el.querySelector("input:not([type=hidden]), textarea")?.focus()
  }

  // Hide a panel by id (Cancel buttons). params: id
  hide(event) {
    const el = event.params.id && document.getElementById(event.params.id)
    el?.classList.add("hidden")
  }

  // Toggle a panel by id (Add attendee / Add addendum). params: id
  toggle(event) {
    const el = event.params.id && document.getElementById(event.params.id)
    el?.classList.toggle("hidden")
  }

  // Hide self — used on a modal backdrop click.
  hideSelf(event) {
    event.currentTarget.classList.add("hidden")
  }

  // Stop a click bubbling to the backdrop — used on the modal content wrapper.
  stop(event) {
    event.stopPropagation()
  }

  // Minute "kind" segmented picker: activate the clicked button, reset its
  // siblings, and set the hidden kind field (firing change so the meeting-item
  // controller updates the dependent fields). Reads data-kind off the button.
  pickKind(event) {
    const btn = event.currentTarget
    const ON = ["bg-brand", "text-white", "border-brand"]
    const OFF = ["bg-white", "border-gray-200", "text-gray-700"]
    btn.parentElement.querySelectorAll("button").forEach((b) => {
      b.classList.remove(...ON)
      b.classList.add(...OFF)
    })
    btn.classList.remove(...OFF)
    btn.classList.add(...ON)
    const field = document.getElementById("add-item-kind")
    if (field) {
      field.value = btn.dataset.kind
      field.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  // "Link to an item" menu entry: open the inline meeting-link picker for the row.
  openLink(event) {
    this._closeDropdowns()
    event.currentTarget
      .closest("[data-minute-row]")
      ?.querySelector("[data-meeting-link-target=trigger]")
      ?.click()
  }

  // Fired by the row's start-time countdown (countdown:complete) the moment a
  // meeting begins: reveal its pre-rendered "Happening now" badge and hide the
  // "Scheduled" chip, so the status flips live without a page refresh.
  promoteToLive(event) {
    const row = event.target.closest("tr")
    if (!row) return
    const badge = row.querySelector("[data-meeting-live-badge]")
    if (!badge) return
    badge.classList.remove("hidden")
    row.querySelector("[data-meeting-scheduled-chip]")?.classList.add("hidden")
  }

  _closeDropdowns() {
    document.dispatchEvent(new CustomEvent("dropdown:close"))
  }
}
